import { Component, Input, OnInit } from '@angular/core';
import { SpinnerService } from './spinner.service';
import { Observable } from 'rxjs';

/**
 * Componente visual que muestra un spinner (`mat-spinner`) según el estado asociado a un `spinnerId`.
 * El estado se gestiona a través del `SpinnerService`.
 *
 * @example
 * ```html
 * <app-spinner spinnerId="modal"></app-spinner>
 * ```
 */
@Component({
  selector: 'app-spinner',
  template: `
    <div *ngIf="spinnerVisible$ | async" [ngClass]="getClassForSpinner()">
      <mat-spinner [color]="getColorForSpinner()" diameter="50" strokeWidth="3"></mat-spinner>
    </div>
  `
})
export class SpinnerComponent implements OnInit {
  /**
   * Identificador único del spinner. Usado por el `SpinnerService` para determinar su visibilidad.
   * Puede ser: `'modal'`, `'fullpage'`, o cualquier otro identificador personalizado.
   */
  @Input() spinnerId!: string;

  /**
   * Observable que indica si el spinner debe mostrarse o no.
   */
  spinnerVisible$!: Observable<boolean>;

  /**
   * Crea una instancia del componente e inyecta el `SpinnerService`.
   * @param spinnerService Servicio que controla el estado de los spinners.
   */
  constructor(private spinnerService: SpinnerService) { }

  /**
   * Inicializa el componente. Se suscribe al estado del spinner basado en el `spinnerId` proporcionado.
   */
  ngOnInit(): void {
    if (this.spinnerId) {
      this.spinnerVisible$ = this.spinnerService.getSpinnerState(this.spinnerId);
    } else {
      console.warn('SpinnerComponent: spinnerId is required');
    }
  }

  /**
   * Retorna la clase CSS a aplicar al contenedor del spinner según su tipo.
   * @returns Clase CSS correspondiente.
   */
  getClassForSpinner(): string {
    switch (this.spinnerId) {
      case 'modal':
        return 'loading-mask spinner-container top-right';
      case 'fullpage':
        return 'loading-mask spinner-container full';
      default:
        return 'loading-mask spinner-container spinner-wrapper';
    }
  }

  /**
   * Define el color del `mat-spinner` dependiendo del tipo de spinner.
   * @returns Color Angular Material: `'primary'`, `'accent'` o `'warn'`.
   */
  getColorForSpinner(): 'primary' | 'accent' | 'warn' {
    switch (this.spinnerId) {
      case 'modal': return 'accent';
      case 'fullpage': return 'warn';
      default: return 'primary';
    }
  }
}
